import { Component, DestroyRef, inject, OnInit, OnDestroy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TimeoutableComponent } from 'src/shared/components/timeoutable/timeoutable.component';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { NotificationSettingsPage } from '../notification-settings/notification-settings';
import { FormBuilder, FormGroup } from '@angular/forms';
import { NotificationsRedirectService } from 'src/providers/notifications/notifications-redirect';
import { Util } from 'src/shared/util/util';
import { Loading } from 'src/shared/components/loading/loading';
import { lastValueFrom, Subscription } from 'rxjs';
import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { NotificationsQueriesService } from 'src/shared/services/notifications-queries.service';
import { NotificationsApiService, Notification } from 'src/shared/services/notifications-api.service';
import { SseService } from 'src/shared/services/sse.service';
import { TranslationPipe } from 'src/shared/pipes/translation.pipe';
import { ConciergeApiService } from 'src/shared/services/concierge-api.service';
import { DisputeResolutionPage } from 'src/pages/concierge/dispute-resolution/dispute-resolution';

@Component({
    templateUrl: 'notification-center.html',
    styleUrls: ['notification-center.scss'],
    standalone: false
})

export class NotificationCenterPage extends TimeoutableComponent implements OnInit, OnDestroy {

  backPage: string;
  allNotifications: any[] = [];
  filteredNotifications: any[] = [];
  totalNotifications: number;
  errorMessage: string;
  isRightSideContent = true;
  searchForm: FormGroup;
  filterForm: FormGroup;
  notificationTypes: any[] = [];
  queryNotifications: any;
  private dropDownMenuSubscription: Subscription;
  showExamples: boolean;
  examples: any;
  showKeys: boolean;
  pageNumber = 1;
  notificationCenterDropDownMenu = notificationCenterDropDownMenu;
  private readonly sseService = inject(SseService);
  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private notificationsApi: NotificationsApiService,
    private notificationsQueries: NotificationsQueriesService,
    private storage: TidyStorage,
    private rightSidePanelService: RightSidePanelService,
    private formBuilder: FormBuilder,
    private notificationsRedirectService: NotificationsRedirectService,
    private util: Util,
    private navCtrl: CustomNavController,
    private conciergeApi: ConciergeApiService
  ) {
    super();
    this.searchForm = this.formBuilder.group({
      search: [''],
    });
    this.filterForm = this.formBuilder.group({
      notification_type: [''],
    });
    this.watchSearchForm();
    this.loadNotificationTypes();
  }

  async ngOnInit() {
    this.backPage = this.navCtrl.getParam('backPage');
    this.isRightSideContent = await this.storage.retrieve('dialog-right-side-open') || false;
    this.rightSidePanelService.setDialogPageTitle('Notifications');
    await this.loadNotifications();
    this.examples = this.buildExamples();
    this.loaded = true;
    this.dropDownMenuSubscription = this.rightSidePanelService.dropDownMenuAction$.subscribe((action) => {
      this.runMeanuAction(action);
    });
    this.sseService.on('notification.received')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.pageNumber = 1;
        this.loadNotifications();
      });
  }

  ngOnDestroy() {
    if (this.dropDownMenuSubscription) {
      this.dropDownMenuSubscription.unsubscribe();
    }
  }

  async loadNotifications() {
    try {
      const notifications = await lastValueFrom(this.notificationsApi.getNotifications(this.pageNumber, 100));
      this.totalNotifications = Number(notifications.totalRecords) || 0;
      this.allNotifications = Array.isArray(notifications.body) ? notifications.body : [];
      this.allNotifications.map((notification: Notification & { icon?: string }) => {
        notification.icon = this.getIcon(notification);
      });
      this.allNotifications = this.groupMessages();
      this.filteredNotifications = this.allNotifications;
    } catch (err) {
      this.errorMessage = (err.error && err.error.message) ? err.error.message : err.message;
    }
  }

  groupMessages() {
    if (!this.allNotifications || this.allNotifications.length === 0) {
      return this.allNotifications;
    }
    const groupedNotifications = [];
    let i = 0;
    while (i < this.allNotifications.length) {
      const currentNotification = this.allNotifications[i];
      const currentChatRoomKey = currentNotification?.page_data?.data?.chat_room_key;
      // If chat_room_key is undefined or null, add notification as is and continue
      if (!currentChatRoomKey) {
        groupedNotifications.push(currentNotification);
        i++;
        continue;
      }
      // Initialize groupedMessagesArray for the current notification
      currentNotification.groupedMessagesArray = [];
      // Look ahead for consecutive notifications with the same chat_room_key
      let j = i + 1;
      while (j < this.allNotifications.length) {
        const nextNotification = this.allNotifications[j];
        const nextChatRoomKey = nextNotification?.page_data?.data?.chat_room_key;
        // If the next notification has the same chat_room_key, group it
        if (nextChatRoomKey && nextChatRoomKey === currentChatRoomKey) {
          currentNotification.groupedMessagesArray.push(nextNotification);
          j++;
        } else {
          // Different chat_room_key found, stop grouping
          break;
        }
      }
      // Add the current notification (with its grouped messages) to the result
      groupedNotifications.push(currentNotification);
      // Move the index to the next ungrouped notification
      i = j;
    }
    return groupedNotifications;
  }

  async loadMoreNotifications(event) {
    setTimeout(async () => {
      if (this.filteredNotifications.length >= this.totalNotifications) {
        return event.target.disabled = true;
      }
      this.pageNumber++;
      try {
        const newNotifications = await lastValueFrom(this.notificationsApi.getNotifications(this.pageNumber, 100));
        newNotifications.body.map((notification: Notification & { icon?: string }) => {
          notification.icon = this.getIcon(notification);
        });
        this.filteredNotifications = [
          ...this.filteredNotifications,
          ...newNotifications.body
        ];
        if (this.filteredNotifications.length >= this.totalNotifications) {
          event.target.disabled = true;
        }
      } catch (err) {
        // Roll back so the next scroll retries this page instead of skipping it.
        this.pageNumber--;
        this.errorMessage = (err.error && err.error.message) ? err.error.message : err.message;
      } finally {
        event.target.complete();
      }
    }, 500);
  }

  parseNotifications(notifications: any[]) {
    return notifications.map((notification) => ({
      ...notification,
      title: notification.title.replaceAll('_', ' ').replaceAll('.', ' '),
      channels: this.parseChannels(
        notification.active_channels,
        notification.available_channels
      ),
      allowsTimeRangeConfig: notification.possible_settings?.includes(
        'allowed_time_ranges'
      ),
    }));
  }

  parseChannels(activeChannels: any[], availableChannels: any[]) {
    return availableChannels.map((channel) => ({
      name: channel.replaceAll('_', ' ').replaceAll('.', ' '),
      checked: activeChannels.includes(channel),
      isOpened: false,
    }));
  }

  @Loading('', true)
  async goToNotificationPage(notification: any) {
    let newlyReadNotifications = 0;
    if (!notification.is_read) {
      lastValueFrom(this.notificationsApi.markAsRead(notification.id));
      notification.is_read = true;
      newlyReadNotifications++;
    }
    if (notification.groupedMessagesArray) {
      notification.groupedMessagesArray.map((n: Notification) => {
        if (!n.is_read) {
          lastValueFrom(this.notificationsApi.markAsRead(n.id));
          n.is_read = true;
          newlyReadNotifications++;
        }
      });
    }
    if (newlyReadNotifications > 0) {
      this.notificationsQueries.invalidateNotifications();
    }
    const disputeTemplateKeys = [
      'dispute.cleaning.customer.counter_proposal',
      'dispute.cleaning.customer.opened',
      'dispute.cleaning.customer.won',
      'dispute.cleaning.customer.lost',
    ];
    if (disputeTemplateKeys.includes(notification?.template_key)) {
      await this.goToDisputeResolution();
      return;
    }
    if (!this.hasNotificationAction(notification)) {
      return;
    }
    notification.page_data.data['notification'] = notification;
    await this.notificationsRedirectService.redirectToPage(notification.page_data);
  }


  private async goToDisputeResolution(): Promise<void> {
    try {
      const response = await lastValueFrom(
        this.conciergeApi.getConciergeItems('&is_marked_done=false&per_page=20')
      );
      const dispute = response.body.find(item => item.type?.startsWith('dispute.'));
      if (!dispute) {
        this.util.showError('No pending dispute found.');
        return;
      }
      const params = { dispute, backPage: 'notification-center' };
      this.rightSidePanelService.navigateTo(
        `dispute-resolution/${dispute.id}`, params, DisputeResolutionPage
      );
    } catch (err) {
      this.util.showError((err?.error?.message) || err?.message || 'Failed to load dispute.');
    }
  }

  async openRightSidePanel(params, component) {
    await this.storage.save('shouldUpdateItems', false);
    await this.storage.save('dialog-params', params);
    await this.storage.save('dialog-right-side-open', true);
    this.rightSidePanelService.openRightSidePanel(component);
  }

  goToNotificationSettings() {
    this.rightSidePanelService.dropDownMenuAction$.next(null);
    this.rightSidePanelService.navigateTo('notification-settings', {}, NotificationSettingsPage);
  }

  watchSearchForm() {
    this.searchForm.valueChanges.subscribe((value) => {
      this.filterForm.patchValue({
        notification_type: ''
      });
      if (value === '' || value === null) {
        this.filteredNotifications = this.allNotifications;
        return;
      }
      this.filteredNotifications = this.allNotifications.filter((notification) => notification.title.toLowerCase().includes(value.search.toLowerCase()));
    });
  }

  async runMeanuAction(action: string) {
    try {
      if (action?.includes('notificationCenter-')) {
        const actionToCall = action.replace('notificationCenter-', '');
        await this[actionToCall]();
      }
    } catch (err) {
      this.util.showError(err)
    }
  }

  async markAllAsRead() {
    await lastValueFrom(this.notificationsApi.markAllAsRead());
    this.notificationsQueries.invalidateNotifications();
    await this.loadNotifications();
  }

  buildExamples() {
    const examples = [];
    const unassignedExamples = [];
    examplesData.forEach((example) => {
      const icon = this.getIcon(example);
      example['icon'] = icon;
      if (icon) {
        if (icon === 'assets/img/notifications/unassigned-success.svg' ||
            icon === 'assets/img/notifications/unassigned.svg') {
          unassignedExamples.push(example);
        } else {
          examples.push(example);
        }
      }
    });
    return [...examples, ...unassignedExamples];
  }

  getIcon(notification) {
    const isMessageFromConcierge = notification?.page_data?.data?.chat_room_type === 'concierge_customer';
    if (isMessageFromConcierge) {
      return 'assets/img/concierge.svg';
    }
    const isJobUpdate = notification?.template_key === 'customer.chat.message_received' && notification?.page_data?.name === 'no_page';
    if (isJobUpdate) {
      return 'assets/img/information-blue.svg';
    }
    return notificationCenterIcons.find((icon) => icon.keys.includes(notification.template_key))?.icon || 'assets/img/message.svg';
  }

  changeNotificationType(value) {
    if (!value || value === '') {
      this.filteredNotifications = this.allNotifications;
      return;
    }
    const notifications = this.allNotifications.filter((notification) => notification.page_data.name === value);
    this.filteredNotifications = notifications;
  }

  loadNotificationTypes() {
    const typeLabels = {
      '': new TranslationPipe().transform('All'),
      'customer_booking_page': new TranslationPipe().transform('Bookings'),
      'address_page': new TranslationPipe().transform('Addresses'),
      'homekeeper_page': new TranslationPipe().transform('Pros'),
      'guest_reservation_source_page': new TranslationPipe().transform('Reservations'),
      'proposal_page': new TranslationPipe().transform('Proposals'),
      'invoice_page': new TranslationPipe().transform('Invoices'),
    }
    this.notificationTypes = Object.keys(typeLabels).map((type) => ({
        viewValue: typeLabels[type],
        value: type
      }));
  }

  hasNotificationAction(notification: any) {
    return notification?.page_data?.name !== 'no_page' && notification?.page_date?.data?.chat_room_type !== 'concierge_customer';
  }
}

export const notificationCenterDropDownMenu = [
  {
    label: new TranslationPipe().transform('Mark All As Read'),
    action: 'notificationCenter-markAllAsRead'
  },
  {
    label: new TranslationPipe().transform('Notification Settings'),
    action: 'notificationCenter-goToNotificationSettings'
  },
]

export const notificationCenterIcons = [
  {
    icon: 'assets/img/message.svg',
    keys: [
      'customer.chat.message_received',
    ]
  },
  {
    icon: 'assets/img/notifications/alert-notification.svg',
    keys: [
      'customer-account-cancellation-email',
      'customer.guest_reservation_source_has_been_paused'
    ]
  },
  {
    icon: 'assets/img/notifications/question-circle.svg',
    keys: [
      'homekeeper-to-customer-lost-item-flow',
    ]
  },
  {
    icon: 'assets/img/notifications/welcome-notification.svg',
    keys: [
      'customer.referral-code-skip-password', //Ark welcomes you to TIDY!
    ]
  },
  {
    icon: 'assets/img/notifications/cant-find-home-notification.svg',
    keys: [
      'change-performance-cant-find-home-v2',
      'change-performance-no-access-v2'
    ]
  },
  {
    icon: 'assets/img/notifications/integration-removed-notification.svg',
    keys: [
      'customer.guest_reservation_source_deactivated',
    ]
  },
  {
    icon: 'assets/img/notifications/properties-updated-notification.svg',
    keys: [
      'customer.guest_reservation_addresses_were_removed_from_the_source',
      'customer.guest_reservation_addresses_were_updated_in_the_source'
    ]
  },
  {
    // Inquiries: guest submitted a booking request/inquiry on one of the host's
    // channels (direct booking, Airbnb, VRBO, …). BACKEND CONTRACT: these keys
    // must match the gotidy notification type once the Inquiry/notification
    // backend ships (card d9rpG2zZ / Danillo's reframe).
    icon: 'assets/svg/calendar-outline.svg',
    keys: [
      'customers.guest_booking_inquiry',
      'customer.direct_booking_request'
    ]
  },
  {
    icon: 'assets/img/notifications/job-completed.svg',
    keys: [
      'customers.cleaning_certificate',
      'client-cleaning-certificate-v3'
    ]
  },
  {
    icon: 'assets/img/paper-plane.svg',
    keys: [
      'customer.proposal_was_created',
    ]
  },
  {
    icon: 'assets/img/notifications/user-added.svg',
    keys: [
      'customer.homekeeper_added_you_as_private_client',
    ]
  },
  {
    icon: 'assets/img/notifications/parking-reimbursement-request.svg',
    keys: [
      'customers.cleanings.parking_reimbursement_initiated',
    ]
  },
  {
    icon: 'assets/img/notifications/feedback-followup.svg',
    keys: [
      'feedback-bad-cleaning',
      'feedback-ask-for-more-details',
      'feedback-you-can-book-more-time'
    ]
  },
  {
    icon: 'assets/img/notifications/dispute-lost.svg',
    keys: [
      'customers.disputes.homekeeper_won',
      'customers.disputes.both_lost',
      'dispute.cleaning.customer.lost'
    ]
  },
  {
    icon: 'assets/img/notifications/dispute-won.svg',
    keys: [
      'customers.disputes.client_won',
      'customers.disputes.both_won',
      'dispute.cleaning.customer.won'
    ]
  },
  {
    icon: 'assets/img/notifications/dispute-response.svg',
    keys: [
      'dispute.cleaning.customer.counter_proposal',
    ]
  },
  {
    icon: 'assets/img/notifications/dispute.svg',
    keys: [
      'dispute.cleaning.customer.opened',
    ]
  },
  {
    icon: 'assets/img/notifications/sync-failed.svg',
    keys: [
      'guest-reservation-source-addresses-sync-failed',
    ]
  },
  {
    icon: 'assets/img/notifications/job-rescheduled.svg',
    keys: [
      'customers.cleanings.reschedule_plan',
      'customers.cleanings.reschedule_cleaning',
      'homekeeper-to-customer-availability-request-accepted'
    ]
  },
  {
    icon: 'assets/img/notifications/job-canceled.svg',
    keys: [
      'customers.cleanings.cancel_cleaning',
      'change-performance-client-refuses-service',
      'homekeeper-to-customer-availability-request-rejected'
    ]
  },
  {
    icon: 'assets/img/search.svg',
    keys: [
      'customer.job_request_created',
    ]
  },
  {
    icon: 'assets/img/notifications/job-scheduled.svg',
    keys: [
      'customers.cleanings.new_opc',
      'customers.cleanings.first_booking', //this seems redundant to new_opc
      'customer.proposal_was_accepted',
      'customer-cleaning-confirmation-v1'
    ]
  },
  {
    icon: 'assets/img/notifications/job-soon.svg',
    keys: [
      'customer-cleaning-reminder-without-instruction',
      'customer-cleaning-reminder-to-dos-off',
      'private-customer-cleaning-reminders',
      'customer-cleaning-reminder-with-instruction-v2'
    ]
  },
  {
    icon: 'assets/img/invoice.svg',
    keys: [
      'send-customer-invoice-from-homekeeper',
      'homekeeper-to-customer-invoice' // this seems redundant to from-homekeeper
    ]
  },
  {
    icon: 'assets/img/notifications/invoice-paid.svg',
    keys: [
      'homekeeper-to-customer-invoice-auto-paid',
    ]
  },
  {
    icon: 'assets/img/notifications/propose-times.svg',
    keys: [
      'homekeeper-to-customer-availability-request-countered',
    ]
  },
  {
    icon: 'assets/img/notifications/unassigned-success.svg',
    keys: [
      'customers.sa.unassigned_success',
      'customers.sa.unassigned_success.1',
      'customers.sa.unassigned_success.2',
      'customers.sa.unassigned_success.3',
      'customers.sa.unassigned_success.4',
      'customers.sa.unassigned_success.5',
      'customers.sa.unassigned_success.6',
      'customers.sa.unassigned_success.7',
      'customers.sa.unassigned_success.8',
      'customers.sa.unassigned_success.9',
      'customers.sa.unassigned_success.10',
      'customers.sa.unassigned_success.11',
      'customers.sa.unassigned_success.12',
      'customers.sa.unassigned_success.13',
      'customers.sa.unassigned_success.14',
      'customers.sa.unassigned_success.15',
      'customers.sa.unassigned_success.16',
      'customers.sa.unassigned_success.17',
      'customers.sa.unassigned_success.18',
      'customers.sa.unassigned_success.19',
      'customers.sa.unassigned_success.20',
      'customers.sa.unassigned_success.21',
      'customers.sa.unassigned_success.22',
      'customers.sa.unassigned_success.23',
      'customers.sa.unassigned_success.24',
      'customers.sa.unassigned_success.25',
      'customers.sa.unassigned_success.26',
      'customers.sa.unassigned_success.27',
      'customers.sa.unassigned_success.28',
      'customers.sa.unassigned_success.29',
      'customers.sa.unassigned_success.30',
      'customers.sa.unassigned_success.31',
      'customers.sa.unassigned_success.32',
      'customers.sa.unassigned_success.33',
      'customers.sa.unassigned_success.34',
      'customers.sa.unassigned_success.35',
      'customers.sa.unassigned_success.36'
    ]
  },
  {
    icon: 'assets/img/notifications/unassigned.svg',
    keys: [
      'customers.sa.unassigned',
      'customers.sa.unassigned.2',
      'customers.sa.unassigned.4',
      'customers.sa.unassigned.6',
      'customers.sa.unassigned.8',
      'customers.sa.unassigned.10',
      'customers.sa.unassigned.12',
      'customers.sa.unassigned.14',
      'customers.sa.unassigned.16',
      'customers.sa.unassigned.18',
      'customers.sa.unassigned.20',
      'customers.sa.unassigned.22',
      'customers.sa.unassigned.25',
      'customers.sa.unassigned.28',
      'customers.sa.unassigned.31',
      'customers.sa.unassigned.34',
      'customers.sa.unassigned.37',
      'customers.sa.unassigned.40',
      'customers.sa.unassigned.43',
      'customers.sa.unassigned.46',
      'customers.sa.unassigned.49',
      'customers.sa.unassigned.51',
      'customers.sa.unassigned.52',
      'customers.sa.unassigned.54',
      'customers.sa.unassigned.57',
      'customers.sa.unassigned.60',
      'customers.sa.unassigned.63',
      'customers.sa.unassigned.66',
      'customers.sa.unassigned.69',
      'customers.sa.unassigned.72',
      'customers.sa.unassigned.75',
      'customers.sa.unassigned.77',
      'customers.sa.unassigned.78',
      'customers.sa.unassigned.80',
      'customers.sa.unassigned.82',
      'customers.sa.unassigned.84',
      'customers.sa.unassigned.86',
      'customers.sa.unassigned.88',
      'customers.sa.unassigned.90',
      'customers.sa.unassigned.92',
      'customers.sa.unassigned.94',
      'customers.sa.unassigned.95',
      'customers.sa.unassigned.96',
      'customers.sa.unassigned.29',
      'customers.sa.unassigned.32',
      'customers.sa.unassigned.35',
      'customers.sa.unassigned.38',
      'customers.sa.unassigned.41',
      'customers.sa.unassigned.44',
      'customers.sa.unassigned.55',
      'customers.sa.unassigned.58',
      'customers.sa.unassigned.61',
      'customers.sa.unassigned.64',
      'customers.sa.unassigned.67',
      'customers.sa.unassigned.70',
      'customers.sa.unassigned.',
      'customers.sa.unassigned.23',
      'customers.sa.unassigned.139',
      'customers.sa.unassigned.140',
      'customers.sa.unassigned.26',
      'customers.sa.unassigned.98',
      'customers.sa.unassigned.99',
      'customers.sa.unassigned.100',
      'customers.sa.unassigned.101',
      'customers.sa.unassigned.102',
      'customers.sa.unassigned.103',
      'customers.sa.unassigned.104',
      'customers.sa.unassigned.105',
      'customers.sa.unassigned.106',
      'customers.sa.unassigned.107',
      'customers.sa.unassigned.108',
      'customers.sa.unassigned.109',
      'customers.sa.unassigned.110',
      'customers.sa.unassigned.111',
      'customers.sa.unassigned.112',
      'customers.sa.unassigned.113',
      'customers.sa.unassigned.114',
      'customers.sa.unassigned.115',
      'customers.sa.unassigned.116',
      'customers.sa.unassigned.117',
      'customers.sa.unassigned.118',
      'customers.sa.unassigned.119',
      'customers.sa.unassigned.120',
      'customers.sa.unassigned.121',
      'customers.sa.unassigned.122',
      'customers.sa.unassigned.123',
      'customers.sa.unassigned.124',
      'customers.sa.unassigned.125',
      'customers.sa.unassigned.126',
      'customers.sa.unassigned.127',
      'customers.sa.unassigned.128',
      'customers.sa.unassigned.129',
      'customers.sa.unassigned.130',
      'customers.sa.unassigned.131',
      'customers.sa.unassigned.132',
      'customers.sa.unassigned.133',
      'customers.sa.unassigned.134',
      'customers.sa.unassigned.135',
      'customers.sa.unassigned.136',
      'customers.sa.unassigned.137',
      'customers.sa.unassigned.138'
    ]
  }
]














const examplesData = [
  {
    notification_key: 'homekeeper-to-customer-invoice-auto-paid',
    notification_title: 'Homekeeper To Customer Invoice Auto Paid',
    email_template_subject: 'Invoice from {{{team_name}}} was paid'
  },
  {
    notification_key: 'customers.sa.unassigned.2',
    notification_title: 'Customers.Sa.Unassigned.2',
    email_template_subject: '{{{email_subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned.4',
    notification_title: 'Customers.Sa.Unassigned.4',
    email_template_subject: '{{{email_subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned.6',
    notification_title: 'Customers.Sa.Unassigned.6',
    email_template_subject: '{{{email_subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned.8',
    notification_title: 'Customers.Sa.Unassigned.8',
    email_template_subject: '{{{email_subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned.10',
    notification_title: 'Customers.Sa.Unassigned.10',
    email_template_subject: '{{{email_subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned.12',
    notification_title: 'Customers.Sa.Unassigned.12',
    email_template_subject: '{{{email_subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned.14',
    notification_title: 'Customers.Sa.Unassigned.14',
    email_template_subject: '{{{email_subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned.16',
    notification_title: 'Customers.Sa.Unassigned.16',
    email_template_subject: '{{{email_subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned.18',
    notification_title: 'Customers.Sa.Unassigned.18',
    email_template_subject: '{{{email_subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned.20',
    notification_title: 'Customers.Sa.Unassigned.20',
    email_template_subject: '{{{email_subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned.22',
    notification_title: 'Customers.Sa.Unassigned.22',
    email_template_subject: '{{{email_subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned.25',
    notification_title: 'Customers.Sa.Unassigned.25',
    email_template_subject: '{{{email_subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned.28',
    notification_title: 'Customers.Sa.Unassigned.28',
    email_template_subject: '{{{email_subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned.31',
    notification_title: 'Customers.Sa.Unassigned.31',
    email_template_subject: '{{{email_subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned.34',
    notification_title: 'Customers.Sa.Unassigned.34',
    email_template_subject: '{{{email_subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned.37',
    notification_title: 'Customers.Sa.Unassigned.37',
    email_template_subject: '{{{email_subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned.40',
    notification_title: 'Customers.Sa.Unassigned.40',
    email_template_subject: '{{{email_subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned.43',
    notification_title: 'Customers.Sa.Unassigned.43',
    email_template_subject: '{{{email_subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned.46',
    notification_title: 'Customers.Sa.Unassigned.46',
    email_template_subject: '{{{email_subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned.49',
    notification_title: 'Customers.Sa.Unassigned.49',
    email_template_subject: '{{{email_subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned.51',
    notification_title: 'Customers.Sa.Unassigned.51',
    email_template_subject: '{{{email_subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned.52',
    notification_title: 'Customers.Sa.Unassigned.52',
    email_template_subject: '{{{email_subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned.54',
    notification_title: 'Customers.Sa.Unassigned.54',
    email_template_subject: '{{{email_subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned.57',
    notification_title: 'Customers.Sa.Unassigned.57',
    email_template_subject: '{{{email_subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned.60',
    notification_title: 'Customers.Sa.Unassigned.60',
    email_template_subject: '{{{email_subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned.63',
    notification_title: 'Customers.Sa.Unassigned.63',
    email_template_subject: '{{{email_subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned.66',
    notification_title: 'Customers.Sa.Unassigned.66',
    email_template_subject: '{{{email_subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned.69',
    notification_title: 'Customers.Sa.Unassigned.69',
    email_template_subject: '{{{email_subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned.72',
    notification_title: 'Customers.Sa.Unassigned.72',
    email_template_subject: '{{{email_subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned.75',
    notification_title: 'Customers.Sa.Unassigned.75',
    email_template_subject: '{{{email_subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned.77',
    notification_title: 'Customers.Sa.Unassigned.77',
    email_template_subject: '{{{email_subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned.78',
    notification_title: 'Customers.Sa.Unassigned.78',
    email_template_subject: '{{{email_subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned.80',
    notification_title: 'Customers.Sa.Unassigned.80',
    email_template_subject: '{{{email_subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned.82',
    notification_title: 'Customers.Sa.Unassigned.82',
    email_template_subject: '{{{email_subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned.84',
    notification_title: 'Customers.Sa.Unassigned.84',
    email_template_subject: '{{{email_subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned.86',
    notification_title: 'Customers.Sa.Unassigned.86',
    email_template_subject: '{{{email_subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned.88',
    notification_title: 'Customers.Sa.Unassigned.88',
    email_template_subject: '{{{email_subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned.90',
    notification_title: 'Customers.Sa.Unassigned.90',
    email_template_subject: '{{{email_subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned.92',
    notification_title: 'Customers.Sa.Unassigned.92',
    email_template_subject: '{{{email_subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned.94',
    notification_title: 'Customers.Sa.Unassigned.94',
    email_template_subject: '{{{email_subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned.95',
    notification_title: 'Customers.Sa.Unassigned.95',
    email_template_subject: '{{{email_subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned.96',
    notification_title: 'Customers.Sa.Unassigned.96',
    email_template_subject: '{{{email_subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned.29',
    notification_title: 'Customers.Sa.Unassigned.29',
    email_template_subject: '{{{email_subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned.32',
    notification_title: 'Customers.Sa.Unassigned.32',
    email_template_subject: '{{{email_subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned.35',
    notification_title: 'Customers.Sa.Unassigned.35',
    email_template_subject: '{{{email_subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned.38',
    notification_title: 'Customers.Sa.Unassigned.38',
    email_template_subject: '{{{email_subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned.41',
    notification_title: 'Customers.Sa.Unassigned.41',
    email_template_subject: '{{{email_subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned.44',
    notification_title: 'Customers.Sa.Unassigned.44',
    email_template_subject: '{{{email_subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned.55',
    notification_title: 'Customers.Sa.Unassigned.55',
    email_template_subject: '{{{email_subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned.58',
    notification_title: 'Customers.Sa.Unassigned.58',
    email_template_subject: '{{{email_subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned.61',
    notification_title: 'Customers.Sa.Unassigned.61',
    email_template_subject: '{{{email_subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned.64',
    notification_title: 'Customers.Sa.Unassigned.64',
    email_template_subject: '{{{email_subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned.67',
    notification_title: 'Customers.Sa.Unassigned.67',
    email_template_subject: '{{{email_subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned.70',
    notification_title: 'Customers.Sa.Unassigned.70',
    email_template_subject: '{{{email_subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned.',
    notification_title: 'Customers.Sa.Unassigned.',
    email_template_subject: '{{{email_subject}}}'
  },
  {
    notification_key: 'customers.cleanings.new_opc',
    notification_title: 'Customers.Cleanings.New Opc',
    email_template_subject: 'TIDY Confirmation: You have scheduled a new cleaning'
  },
  {
    notification_key: 'homekeepers.payments.payout_failed',
    notification_title: 'Payout Failed',
    email_template_subject: 'Bank information needed'
  },
  {
    notification_key: 'customers.sa.unassigned.23',
    notification_title: 'Customers.Sa.Unassigned.23',
    email_template_subject: '{{{email_subject}}}'
  },
  {
    notification_key: 'homekeeper-to-customer-availability-request-accepted',
    notification_title: 'Homekeeper To Customer Availability Request Accepted',
    email_template_subject: 'Your pro {{{homekeeper.first_name}}} has successfully been rescheduled'
  },
  {
    notification_key: 'customer.job_request_created',
    notification_title: 'Customer.Job Request Created',
    email_template_subject: 'New job request'
  },
  {
    notification_key: 'customers.sa.unassigned.139',
    notification_title: 'Customers.Sa.Unassigned.139',
    email_template_subject: '{{{email_subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned.140',
    notification_title: 'Customers.Sa.Unassigned.140',
    email_template_subject: '{{{email_subject}}}'
  },
  {
    notification_key: 'homekeeper.job_cancellation',
    notification_title: 'Job Cancelled',
    email_template_subject: 'Job Cancelled'
  },
  {
    notification_key: 'guest-reservation-source-addresses-sync-failed',
    notification_title: 'Guest Reservation Source Addresses Sync Failed',
    email_template_subject: 'Listing not synced'
  },
  {
    notification_key: 'customers.sa.unassigned.26',
    notification_title: 'Customers.Sa.Unassigned.26',
    email_template_subject: '{{{email_subject}}}'
  },
  {
    notification_key: 'customers.cleanings.first_booking',
    notification_title: 'Customers.Cleanings.First Booking',
    email_template_subject: 'TIDY Confirmation: You have scheduled {{{words.pluralization.cleaning}}}'
  },
  {
    notification_key: 'homekeepers.disputes.new_dispute',
    notification_title: 'New Dispute',
    email_template_subject: 'Client Disputed a Cleaning'
  },
  {
    notification_key: 'homekeepers.disputes.homekeeper_won',
    notification_title: 'You Won a Dispute',
    email_template_subject: 'Client dispute found in your favor'
  },
  {
    notification_key: 'homekeepers.disputes.both_won',
    notification_title: 'Homekeepers.Disputes.Both Won',
    email_template_subject: 'Client dispute found in your favor'
  },
  {
    notification_key: 'homekeepers.disputes.client_won',
    notification_title: 'Homekeepers.Disputes.Client Won',
    email_template_subject: 'Client dispute not found in your favor'
  },
  {
    notification_key: 'homekeepers.disputes.both_lost',
    notification_title: 'Homekeepers.Disputes.Both Lost',
    email_template_subject: 'Client dispute not found in your favor'
  },
  {
    notification_key: 'customers.disputes.client_won',
    notification_title: 'Customers.Disputes.Client Won',
    email_template_subject: 'Dispute found in your favor'
  },
  {
    notification_key: 'customers.disputes.both_won',
    notification_title: 'Customers.Disputes.Both Won',
    email_template_subject: 'Dispute found in your favor'
  },
  {
    notification_key: 'customers.disputes.homekeeper_won',
    notification_title: 'Customers.Disputes.Homekeeper Won',
    email_template_subject: 'Dispute not found in your favor'
  },
  {
    notification_key: 'customers.disputes.both_lost',
    notification_title: 'Customers.Disputes.Both Lost',
    email_template_subject: 'Dispute not found in your favor'
  },
  {
    notification_key: 'customers.cleanings.reschedule_plan',
    notification_title: 'Customers.Cleanings.Reschedule Plan',
    email_template_subject: 'TIDY Confirmation: You have rescheduled {{{words.pluralization.cleaning}}}'
  },
  {
    notification_key: 'customers.cleanings.reschedule_cleaning',
    notification_title: 'Customers.Cleanings.Reschedule Cleaning',
    email_template_subject: 'TIDY Confirmation: You have rescheduled a cleaning'
  },
  {
    notification_key: 'customers.cleanings.cancel_cleaning',
    notification_title: 'Customers.Cleanings.Cancel Cleaning',
    email_template_subject: 'TIDY Confirmation: You cancelled a cleaning'
  },
  {
    notification_key: 'customer-to-homekeeper-lost-item-flow',
    notification_title: 'Customer To Homekeeper Lost Item Flow',
    email_template_subject: 'Client {{{customer.first_name}}} is reaching out about a missing item'
  },
  {
    notification_key: 'feedback-bad-cleaning',
    notification_title: 'Feedback Bad Cleaning',
    email_template_subject: 'Following up on your negative review'
  },
  {
    notification_key: 'feedback-ask-for-more-details',
    notification_title: 'Feedback Ask For More Details',
    email_template_subject: 'Following up on your negative review'
  },
  {
    notification_key: 'feedback-you-can-book-more-time',
    notification_title: 'Feedback You Can Book More Time',
    email_template_subject: 'Following up on your negative review'
  },
  {
    notification_key: 'customer-account-cancellation-email',
    notification_title: 'Customer Account Cancellation Email',
    email_template_subject: 'TIDY Account Cancellation Confirmation'
  },
  {
    notification_key: 'customer-to-homekeeper-availability-request-countered',
    notification_title: 'Customer To Homekeeper Availability Request Countered',
    email_template_subject: 'Client {{{customer.display_name}}} proposed different times to reschedule'
  },
  {
    notification_key: 'homekeeper-email-verification-v1',
    notification_title: 'Homekeeper Email Verification V1',
    email_template_subject: 'TIDY Email Confirmation'
  },
  {
    notification_key: 'homekeepers.cleanings.parking_reimbursement_approved',
    notification_title: 'Parking Reimbursement Approved',
    email_template_subject: 'Your parking reimbursement request was approved'
  },
  {
    notification_key: 'homekeepers.cleanings.parking_reimbursement_rejected',
    notification_title: 'Parking Reimbursement Rejected',
    email_template_subject: 'Your parking reimbursement request was rejected'
  },
  {
    notification_key: 'email-warning-v1',
    notification_title: 'Email Warning V1',
    email_template_subject: 'TIDY Email Change to {{{new_email}}}'
  },
  {
    notification_key: 'customers.sa.unassigned.98',
    notification_title: 'Customers.Sa.Unassigned.98',
    email_template_subject: '{{{email_subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned.99',
    notification_title: 'Customers.Sa.Unassigned.99',
    email_template_subject: '{{{email_subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned.100',
    notification_title: 'Customers.Sa.Unassigned.100',
    email_template_subject: '{{{email_subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned.101',
    notification_title: 'Customers.Sa.Unassigned.101',
    email_template_subject: '{{{email_subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned.102',
    notification_title: 'Customers.Sa.Unassigned.102',
    email_template_subject: '{{{email_subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned.103',
    notification_title: 'Customers.Sa.Unassigned.103',
    email_template_subject: '{{{email_subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned.104',
    notification_title: 'Customers.Sa.Unassigned.104',
    email_template_subject: '{{{email_subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned.105',
    notification_title: 'Customers.Sa.Unassigned.105',
    email_template_subject: '{{{email_subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned.106',
    notification_title: 'Customers.Sa.Unassigned.106',
    email_template_subject: '{{{email_subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned.107',
    notification_title: 'Customers.Sa.Unassigned.107',
    email_template_subject: '{{{email_subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned.108',
    notification_title: 'Customers.Sa.Unassigned.108',
    email_template_subject: '{{{email_subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned.109',
    notification_title: 'Customers.Sa.Unassigned.109',
    email_template_subject: '{{{email_subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned.110',
    notification_title: 'Customers.Sa.Unassigned.110',
    email_template_subject: '{{{email_subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned.111',
    notification_title: 'Customers.Sa.Unassigned.111',
    email_template_subject: '{{{email_subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned.112',
    notification_title: 'Customers.Sa.Unassigned.112',
    email_template_subject: '{{{email_subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned.113',
    notification_title: 'Customers.Sa.Unassigned.113',
    email_template_subject: '{{{email_subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned.114',
    notification_title: 'Customers.Sa.Unassigned.114',
    email_template_subject: '{{{email_subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned.115',
    notification_title: 'Customers.Sa.Unassigned.115',
    email_template_subject: '{{{email_subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned.116',
    notification_title: 'Customers.Sa.Unassigned.116',
    email_template_subject: '{{{email_subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned.117',
    notification_title: 'Customers.Sa.Unassigned.117',
    email_template_subject: '{{{email_subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned.118',
    notification_title: 'Customers.Sa.Unassigned.118',
    email_template_subject: '{{{email_subject}}}'
  },
  {
    notification_key: 'homekeeper.customer_added_you_on_tidy_and_in_your_list',
    notification_title: 'Customer Added You',
    email_template_subject: 'The Client {{customer_name}} added you on TIDY'
  },
  {
    notification_key: 'homekeeper-to-customer-lost-item-flow',
    notification_title: 'Homekeeper To Customer Lost Item Flow',
    email_template_subject: 'Your pro {{{homekeeper.first_name}}} is reaching out about a missing item'
  },
  {
    notification_key: 'customers.cleanings.parking_reimbursement_initiated',
    notification_title: 'Customers.Cleanings.Parking Reimbursement Initiated',
    email_template_subject: 'Your pro has a parking reimbursement request'
  },
  {
    notification_key: 'homekeeper-to-customer-availability-request-countered',
    notification_title: 'Homekeeper To Customer Availability Request Countered',
    email_template_subject: 'Your pro {{{homekeeper.first_name}}} proposed different times to reschedule'
  },
  {
    notification_key: 'homekeeper-to-customer-availability-request-rejected',
    notification_title: 'Homekeeper To Customer Availability Request Rejected',
    email_template_subject: 'Your pro {{{homekeeper.first_name}}} could not reschedule'
  },
  {
    notification_key: 'email-resubmit-hk-equipment-v1',
    notification_title: 'Email Resubmit Hk Equipment V1',
    email_template_subject: 'Could Not Certify Equipment'
  },
  {
    notification_key: 'homekeepers.job_delegation_requests.accept_request',
    notification_title: 'Job Delegation Requests',
    email_template_subject: '{{{name_new_homekeeper}}} accepted your cleaning delegation'
  },
  {
    notification_key: 'customers.sa.unassigned.119',
    notification_title: 'Customers.Sa.Unassigned.119',
    email_template_subject: '{{{email_subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned.120',
    notification_title: 'Customers.Sa.Unassigned.120',
    email_template_subject: '{{{email_subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned.121',
    notification_title: 'Customers.Sa.Unassigned.121',
    email_template_subject: '{{{email_subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned.122',
    notification_title: 'Customers.Sa.Unassigned.122',
    email_template_subject: '{{{email_subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned.123',
    notification_title: 'Customers.Sa.Unassigned.123',
    email_template_subject: '{{{email_subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned.124',
    notification_title: 'Customers.Sa.Unassigned.124',
    email_template_subject: '{{{email_subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned.125',
    notification_title: 'Customers.Sa.Unassigned.125',
    email_template_subject: '{{{email_subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned_success.1',
    notification_title: 'Customers.Sa.Unassigned Success.1',
    email_template_subject: '{{{subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned_success.2',
    notification_title: 'Customers.Sa.Unassigned Success.2',
    email_template_subject: '{{{subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned_success.3',
    notification_title: 'Customers.Sa.Unassigned Success.3',
    email_template_subject: '{{{subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned_success.4',
    notification_title: 'Customers.Sa.Unassigned Success.4',
    email_template_subject: '{{{subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned_success.5',
    notification_title: 'Customers.Sa.Unassigned Success.5',
    email_template_subject: '{{{subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned_success.6',
    notification_title: 'Customers.Sa.Unassigned Success.6',
    email_template_subject: '{{{subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned_success.7',
    notification_title: 'Customers.Sa.Unassigned Success.7',
    email_template_subject: '{{{subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned_success.8',
    notification_title: 'Customers.Sa.Unassigned Success.8',
    email_template_subject: '{{{subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned_success.9',
    notification_title: 'Customers.Sa.Unassigned Success.9',
    email_template_subject: '{{{subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned_success.10',
    notification_title: 'Customers.Sa.Unassigned Success.10',
    email_template_subject: '{{{subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned_success.11',
    notification_title: 'Customers.Sa.Unassigned Success.11',
    email_template_subject: '{{{subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned_success.12',
    notification_title: 'Customers.Sa.Unassigned Success.12',
    email_template_subject: '{{{subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned_success.13',
    notification_title: 'Customers.Sa.Unassigned Success.13',
    email_template_subject: '{{{subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned_success.14',
    notification_title: 'Customers.Sa.Unassigned Success.14',
    email_template_subject: '{{{subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned_success.15',
    notification_title: 'Customers.Sa.Unassigned Success.15',
    email_template_subject: '{{{subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned_success.16',
    notification_title: 'Customers.Sa.Unassigned Success.16',
    email_template_subject: '{{{subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned_success.17',
    notification_title: 'Customers.Sa.Unassigned Success.17',
    email_template_subject: '{{{subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned_success.18',
    notification_title: 'Customers.Sa.Unassigned Success.18',
    email_template_subject: '{{{subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned_success.19',
    notification_title: 'Customers.Sa.Unassigned Success.19',
    email_template_subject: '{{{subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned_success.20',
    notification_title: 'Customers.Sa.Unassigned Success.20',
    email_template_subject: '{{{subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned_success.21',
    notification_title: 'Customers.Sa.Unassigned Success.21',
    email_template_subject: '{{{subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned_success.22',
    notification_title: 'Customers.Sa.Unassigned Success.22',
    email_template_subject: '{{{subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned_success.23',
    notification_title: 'Customers.Sa.Unassigned Success.23',
    email_template_subject: '{{{subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned_success.24',
    notification_title: 'Customers.Sa.Unassigned Success.24',
    email_template_subject: '{{{subject}}}'
  },
  {
    notification_key: 'homekeeper.team.member.invitation',
    notification_title: 'Homekeeper.Team.Member.Invitation',
    email_template_subject: '{{{team_owner_first_name}}} has invited you to their team: {{{team_name}}}'
  },
  {
    notification_key: 'customers.sa.unassigned.126',
    notification_title: 'Customers.Sa.Unassigned.126',
    email_template_subject: '{{{email_subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned.127',
    notification_title: 'Customers.Sa.Unassigned.127',
    email_template_subject: '{{{email_subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned.128',
    notification_title: 'Customers.Sa.Unassigned.128',
    email_template_subject: '{{{email_subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned.129',
    notification_title: 'Customers.Sa.Unassigned.129',
    email_template_subject: '{{{email_subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned.130',
    notification_title: 'Customers.Sa.Unassigned.130',
    email_template_subject: '{{{email_subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned.131',
    notification_title: 'Customers.Sa.Unassigned.131',
    email_template_subject: '{{{email_subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned.132',
    notification_title: 'Customers.Sa.Unassigned.132',
    email_template_subject: '{{{email_subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned.133',
    notification_title: 'Customers.Sa.Unassigned.133',
    email_template_subject: '{{{email_subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned.134',
    notification_title: 'Customers.Sa.Unassigned.134',
    email_template_subject: '{{{email_subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned.135',
    notification_title: 'Customers.Sa.Unassigned.135',
    email_template_subject: '{{{email_subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned.136',
    notification_title: 'Customers.Sa.Unassigned.136',
    email_template_subject: '{{{email_subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned.137',
    notification_title: 'Customers.Sa.Unassigned.137',
    email_template_subject: '{{{email_subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned_success.25',
    notification_title: 'Customers.Sa.Unassigned Success.25',
    email_template_subject: '{{{subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned_success.26',
    notification_title: 'Customers.Sa.Unassigned Success.26',
    email_template_subject: '{{{subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned_success.27',
    notification_title: 'Customers.Sa.Unassigned Success.27',
    email_template_subject: '{{{subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned_success.28',
    notification_title: 'Customers.Sa.Unassigned Success.28',
    email_template_subject: '{{{subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned_success.29',
    notification_title: 'Customers.Sa.Unassigned Success.29',
    email_template_subject: '{{{subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned_success.30',
    notification_title: 'Customers.Sa.Unassigned Success.30',
    email_template_subject: '{{{subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned_success.31',
    notification_title: 'Customers.Sa.Unassigned Success.31',
    email_template_subject: '{{{subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned_success.32',
    notification_title: 'Customers.Sa.Unassigned Success.32',
    email_template_subject: '{{{subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned_success.33',
    notification_title: 'Customers.Sa.Unassigned Success.33',
    email_template_subject: '{{{subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned_success.34',
    notification_title: 'Customers.Sa.Unassigned Success.34',
    email_template_subject: '{{{subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned_success.35',
    notification_title: 'Customers.Sa.Unassigned Success.35',
    email_template_subject: '{{{subject}}}'
  },
  {
    notification_key: 'customers.sa.unassigned_success.36',
    notification_title: 'Customers.Sa.Unassigned Success.36',
    email_template_subject: '{{{subject}}}'
  },
  {
    notification_key: 'email-verification-v1',
    notification_title: 'Email Verification V1',
    email_template_subject: 'Email Verification'
  },
  {
    notification_key: 'homekeeper.tidy_added_you_on_job_by_vendor_outreach_request',
    notification_title: 'Homekeeper.Tidy Added You On Job By Vendor Outreach Request',
    email_template_subject: 'We have a job for you!'
  },
  {
    notification_key: 'customer.proposal_was_accepted',
    notification_title: 'Customer.Proposal Was Accepted',
    email_template_subject: 'Your Accepted {{service_type_name}} Proposal'
  },
  {
    notification_key: 'homekeeper.proposal_was_accepted',
    notification_title: 'Proposal Was Accepted',
    email_template_subject: 'Your proposal with {{customer_full_name}} was accepted'
  },
  {
    notification_key: 'customer.homekeeper_added_you_as_private_client',
    notification_title: 'Customer.Homekeeper Added You As Private Client',
    email_template_subject: 'The pro {{homekeeper_name}} added you'
  },
  {
    notification_key: 'homekeeper.customer_added_you_on_private_list',
    notification_title: 'Homekeeper.Customer Added You On Private List',
    email_template_subject: 'Your client {{customer_name}} added you to their favorites in TIDY'
  },
  {
    notification_key: 'homekeepers.job_delegation_requests.reject_request',
    notification_title: 'Job Delegation Request Rejected',
    email_template_subject: '{{{name_new_homekeeper}}} rejected your job delegation'
  },
  {
    notification_key: 'customer.proposal_was_created',
    notification_title: 'Customer.Proposal Was Created',
    email_template_subject: 'You received a Proposal for {{service_type_name}}'
  },
  {
    notification_key: 'background-check-approved-v1',
    notification_title: 'Background Check Approved V1',
    email_template_subject: 'Your background check was approved.'
  },
  {
    notification_key: 'customers.cleaning_certificate',
    notification_title: 'Customers.Cleaning Certificate',
    email_template_subject: '{{email_subject}}'
  },
  {
    notification_key: 'customers.sa.unassigned.138',
    notification_title: 'Customers.Sa.Unassigned.138',
    email_template_subject: '{{{email_subject}}}'
  },
  {
    notification_key: 'send-customer-invoice-from-homekeeper',
    notification_title: 'Send Customer Invoice From Homekeeper',
    email_template_subject: 'Invoice from {{team_name}}'
  },
  {
    notification_key: 'homekeeper-to-customer-invoice',
    notification_title: 'Homekeeper To Customer Invoice',
    email_template_subject: 'Invoice from {{{team_name}}}'
  },
  {
    notification_key: 'dispute.cleaning.customer.opened',
    notification_title: 'Dispute.Cleaning.Customer.Opened',
    email_template_subject: 'Dispute Opened'
  },
  {
    notification_key: 'dispute.cleaning.customer.counter_proposal',
    notification_title: 'Dispute.Cleaning.Customer.Counter Proposal',
    email_template_subject: 'Dispute Response (Time Sensitive)'
  },
  {
    notification_key: 'dispute.cleaning.customer.won',
    notification_title: 'Dispute.Cleaning.Customer.Won',
    email_template_subject: 'Dispute Won'
  },
  {
    notification_key: 'dispute.cleaning.customer.lost',
    notification_title: 'Dispute.Cleaning.Customer.Lost',
    email_template_subject: 'Dispute Resolved'
  },
  {
    notification_key: 'dispute.cleaning.homekeeper.opened',
    notification_title: 'Dispute.Cleaning.Homekeeper.Opened',
    email_template_subject: 'Dispute Opened (Time Sensitive)'
  },
  {
    notification_key: 'dispute.cleaning.homekeeper.counter_proposal',
    notification_title: 'Dispute.Cleaning.Homekeeper.Counter Proposal',
    email_template_subject: 'Dispute Response Sent'
  },
  {
    notification_key: 'dispute.cleaning.homekeeper.won',
    notification_title: 'Dispute.Cleaning.Homekeeper.Won',
    email_template_subject: 'Dispute Won'
  },
  {
    notification_key: 'homekeeper.opportunities_summary',
    notification_title: 'Open Job Opportunities Summary',
    email_template_subject: '{{opportunities_count}} jobs match your settings {{full_date_formatted}}.'
  },
  {
    notification_key: 'customer.guest_reservation_addresses_were_removed_from_the_source',
    notification_title: 'Customer.Guest Reservation Addresses Were Removed From The Source',
    email_template_subject: '{{amount_addresses_removed}} {{#is_one_address}}property{{/is_one_address}}{{^is_one_address}}properties{{/is_one_address}} removed from {{source_name}}'
  },
  {
    notification_key: 'client-cleaning-certificate-v3',
    notification_title: 'Client Cleaning Certificate V3',
    email_template_subject: 'Cleaning Certificate for your {{job_type}} today'
  },
  {
    notification_key: 'customer.guest_reservation_addresses_were_updated_in_the_source',
    notification_title: 'Customer.Guest Reservation Addresses Were Updated In The Source',
    email_template_subject: '{{amount_addresses_removed}} {{#is_one_address}}property{{/is_one_address}}{{^is_one_address}}properties{{/is_one_address}} updated in {{source_name}}'
  },
  {
    notification_key: 'homekeeper.after-customer-sent-private-payment-to-homekeeper',
    notification_title: 'Payment Sent',
    email_template_subject: '${{job_amount}} payment received from {{customer_name}}'
  },
  {
    notification_key: 'customer.guest_reservation_source_has_been_paused',
    notification_title: 'Customer.Guest Reservation Source Has Been Paused',
    email_template_subject: 'Your {{integration_name}} integration with TIDY had an error'
  },
  {
    notification_key: 'customer.guest_reservation_source_deactivated',
    notification_title: 'Customer.Guest Reservation Source Deactivated',
    email_template_subject: 'You\'ve successfully removed an integration from TIDY'
  },
  {
    notification_key: 'change-performance-cant-find-home-v2',
    notification_title: 'Change Performance Cant Find Home V2',
    email_template_subject: 'Your {{customer.homekeeper_type}} Is Having Trouble Finding your {{customer.home_type}} {{ENV_INFO}}'
  },
  {
    notification_key: 'change-performance-client-refuses-service',
    notification_title: 'Change Performance Client Refuses Service',
    email_template_subject: 'Your {{customer.homekeeper_type}} Reported That You Cancelled Your Cleaning'
  },
  {
    notification_key: 'change-performance-no-access-v2',
    notification_title: 'Change Performance No Access V2',
    email_template_subject: 'Your {{customer.homekeeper_type}} Is Having Trouble Accessing Your {{customer.home_type}} {{ENV_INFO}}'
  },
  {
    notification_key: 'customer-cleaning-confirmation-v1',
    notification_title: 'Customer Cleaning Confirmation V1',
    email_template_subject: 'Cleaning Confirmation'
  },
  {
    notification_key: 'waterfall.homekeeper.ecr_created_for_priority',
    notification_title: 'New Opportunity from Past Client',
    email_template_subject: 'New opportunity for you!'
  },
  {
    notification_key: 'dispute.cleaning.homekeeper.lost',
    notification_title: 'Dispute.Cleaning.Homekeeper.Lost',
    email_template_subject: 'Dispute Lost'
  },
  {
    notification_key: 'customer-to-homekeeper-availability-request-initiated-no-job',
    notification_title: 'Customer To Homekeeper Availability Request Initiated No Job',
    email_template_subject: 'Your Client {{{customer.display_name}}} requested you!'
  },
  {
    notification_key: 'email-approved-hk-equipment-review-v1',
    notification_title: 'Email Approved Hk Equipment Review V1',
    email_template_subject: 'Equipment Certified'
  },
  {
    notification_key: 'homekeeper.job_booked',
    notification_title: 'Job Booked',
    email_template_subject: 'Client booked you through TIDY: {{job_full_date_time}}'
  },
  {
    notification_key: 'customer.referral-code-skip-password',
    notification_title: 'Customer.Referral Code Skip Password',
    email_template_subject: 'Ark welcomes you to TIDY!'
  },
  {
    notification_key: 'hk-in-collections',
    notification_title: 'Hk In Collections',
    email_template_subject: 'You are no longer eligible for equipment'
  },
  {
    notification_key: 'homekeeper-no-show-v1',
    notification_title: 'Homekeeper No Show V1',
    email_template_subject: 'Your {{datetime}} cleaning was updated'
  },
  {
    notification_key: 'customer-cleaning-reminder-without-instruction',
    notification_title: 'Customer Cleaning Reminder Without Instruction',
    email_template_subject: 'Friendly Reminder - Your {{job.service_type_name}} is {{job.dow}}, {{job.date}} at {{job.time}}'
  },
  {
    notification_key: 'customer-cleaning-reminder-to-dos-off',
    notification_title: 'Customer Cleaning Reminder To Dos Off',
    email_template_subject: 'Friendly Reminder - Your {{job.service_type_name}} is {{job.dow}}, {{job.date}} at {{job.time}}'
  },
  {
    notification_key: 'homekeeper-tomorrow-cleanings-v2',
    notification_title: 'Tomorrow\'s Cleanings Reminder',
    email_template_subject: 'Your Scheduled Cleanings on {{on_date}}'
  },
  {
    notification_key: 'hk-incompleted-cleaning',
    notification_title: 'Hk Incompleted Cleaning',
    email_template_subject: 'Note about a job today'
  },
  {
    notification_key: 'private-customer-cleaning-reminders',
    notification_title: 'Private Customer Cleaning Reminders',
    email_template_subject: 'Friendly Reminder - Your {{job.service_type_name}} is {{job.dow}}, {{job.date}} at {{job.time}}'
  },
  {
    notification_key: 'customer-cleaning-reminder-with-instruction-v2',
    notification_title: 'Customer Cleaning Reminder With Instruction V2',
    email_template_subject: 'Friendly Reminder - Your {{job.service_type_name}} is {{job.dow}}, {{job.date}} at {{job.time}}'
  },
  {
    notification_key: 'customer-to-homekeeper-availability-request-accepted',
    notification_title: 'Customer To Homekeeper Availability Request Accepted',
    email_template_subject: 'Client {{{customer.display_name}}} has successfully been rescheduled'
  },
  {
    notification_key: 'customer-to-homekeeper-availability-request-initiated',
    notification_title: 'Customer To Homekeeper Availability Request Initiated',
    email_template_subject: 'Your Client {{{customer.display_name}}} requested you!'
  },
  {
    notification_key: 'customer-to-homekeeper-availability-request-rejected',
    notification_title: 'Customer To Homekeeper Availability Request Rejected',
    email_template_subject: 'Client {{{customer.display_name}}} could not reschedule'
  },
  {
    notification_key: 'homekeepers.job_delegation_requests.new_request',
    notification_title: 'Job Delegation Requests',
    email_template_subject: 'New job delegation from {{{name_original_homekeeper}}}'
  },
  {
    notification_key: 'homekeepers.job_delegation_requests.new_request_force_accept',
    notification_title: 'Homekeepers.Job Delegation Requests.New Request Force Accept',
    email_template_subject: 'New job delegation from {{{name_original_homekeeper}}}'
  },
  {
    notification_key: 'customer.chat.message_received',
    notification_title: 'New Message',
    email_template_subject: 'New message'
  },

]


